'use client'

import React, { useEffect, useRef } from 'react'

import * as Sentry from '@sentry/nextjs'
import { useParams, usePathname, useSearchParams } from 'next/navigation'

import { ButtonClick, track, VpvBookingExperience } from '@dfds-frontend/tracking'
import { seabookRouteMap, shortDateFormat } from '@dfds-pax/utils'

import { getSessionBasket } from '@/utils/sessionStorage'

import { getBasket } from '../_actions/getBasket'
import { logFrontendWarning } from '../_lib/frontendLogging'
import { getTrackingData, TrackingData } from './_actions/getTrackingData'
import { getUserId } from './_actions/getUserId'
import { CheckoutSteps, trackCheckout } from './trackCheckout'
import { trackConfirmation } from './trackConfirmation'

interface Props {
  data?: TrackingData
}
const Tracking: React.FC<Props> = ({ data }) => {
  const params = useParams()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const basketId = searchParams.get('basketId')
  const { locale } = params as { locale: string }

  // This state is used to ensure that the VPV tracking is only executed once per page load, even if the component re-renders multiple times. The useRef hook is ideal for this purpose as it allows us to persist a mutable value across renders without causing additional renders when it changes. By storing the contentful IDs that have already been tracked in a Set, we can efficiently check if a particular page has already been tracked and avoid duplicate tracking calls, which could skew analytics data.
  const trackedContentfulIds = useRef<Set<string>>(new Set())

  useEffect(() => {
    const contentfulId = getContentfulId(pathname as string)
    if (trackedContentfulIds.current.has(contentfulId)) return
    trackedContentfulIds.current.add(contentfulId)
    ;(async () => {
      try {
        let _data = data
        if (pathname?.includes('yourbooking')) {
          // yourbooking is the start of the flow and has no basketId in the URL;
          // the basket lives in session storage at this step.
          const yourBookingBasketId = basketId ?? getSessionBasket()?.basketId
          if (!yourBookingBasketId) return
          const basket = await getBasket(yourBookingBasketId)
          _data = await getTrackingData(basket)
        }
        if (!_data) return
        const coutryMarketCode = getCountryMarketCode(locale)
        const languageLocaleCode = getLanguageLocaleCode(locale)
        const currentLocale = getCurrentLocale(locale)
        const userId = await getUserId()

        const outboundDeparture = _data.departures?.length > 0 ? _data.departures[0] : undefined
        const outboundUnloCode = outboundDeparture ? `${outboundDeparture.departurePort}-${outboundDeparture.arrivalPort}` : undefined
        const returnDeparture = _data.departures?.length > 1 ? _data.departures[1] : undefined

        const outboundSeabookRouteCode = seabookRouteMap.find((r) => r.route === outboundUnloCode)?.seabook
        const returnUnloCode = returnDeparture ? `${returnDeparture?.departurePort}-${returnDeparture?.arrivalPort}` : undefined
        const returnSeabookRouteCode = seabookRouteMap.find((r) => r.route === returnUnloCode)?.seabook

        const vpvObject: VpvBookingExperience = {
          bookingEngine: 'PAXOB',
          businessArea: 'passenger',
          contentfulId,
          countryCode: coutryMarketCode,
          currentLocale: currentLocale,
          event: 'vpv',
          isAgent: false,
          isReturnBooking: _data.departures.length > 1 ? 'yes' : 'no',
          languageCode: languageLocaleCode,
          localeCode: languageLocaleCode,
          market: coutryMarketCode,
          numberAdults: _data.numberAdults,
          numberChildren: _data.numberChildren,
          numberInfants: _data.numberInfants,
          offer_code: _data.departures.find((d) => d !== undefined)?.offerCode || undefined,
          outboundDepartureDate: shortDateFormat('en', _data.departures[0]?.departureDate),
          outboundDepartureTime: _data.departures[0]?.departureTime,
          outboundProductCode: _data.departures[0]?.productCode,
          pagePath: pathname as string,
          pageTitle: document.title,
          pets_selected: _data.hasPets,
          productCode: _data.departures[0]?.productCode,
          returnDepartureDate: _data.departures?.length > 1 ? shortDateFormat('en', _data.departures[1]?.departureDate) : undefined,
          returnDepartureTime: _data.departures?.length > 1 ? _data.departures[1]?.departureTime : undefined,
          returnProductCode: _data.departures?.length > 1 ? _data.departures[1]?.productCode || undefined : undefined,
          returnRouteCode: returnSeabookRouteCode,
          returnUnloCode: returnDeparture ? `${returnDeparture.departurePort}-${returnDeparture.arrivalPort}` : undefined,
          routeCode: outboundSeabookRouteCode,
          salesOwner: _data.salesOwner,
          session: _data.session,
          statusCode: '200',
          unloCode: `${outboundDeparture?.departurePort}-${outboundDeparture?.arrivalPort}`,
          userID: userId,
          vehicleType: {
            count: 1,
            trailerType: _data.trailerCode || undefined,
            vehicleType: _data.vehicleCode!,
          },
        }
        track<VpvBookingExperience>(vpvObject)

        if (contentfulId === 'Passengers') {
          trackCheckout(CheckoutSteps.checkout, _data)
        } else if (contentfulId === 'Confirmation') {
          track<ButtonClick>({
            clickedElementText: 'Accepted',
            event: 'buttonClick',
            eventAction: 'paymentState',
          })
          trackConfirmation(_data, _data.basketId)
        }
      } catch (error) {
        // Log tracking errors but don't break the user journey
        logFrontendWarning('Tracking failed', {
          contentfulId,
          errorMessage: (error as Error)?.message,
          pathname,
        })
        Sentry.captureException(error, {
          contexts: {
            tracking: {
              contentfulId,
              pathname,
            },
          },
        })
      }
    })()
    // We only want to run this effect when the pathname changes, which indicates a page navigation. The dependencies for this effect are the pathname and locale, but since locale is derived from the pathname, we can safely ignore it in the dependency array to prevent unnecessary re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return null
}

export default Tracking

const getCountryMarketCode = (locale: string): string => {
  if (locale === 'en') return 'INT'
  if (locale && locale.length > 2) {
    const countryCode = locale.slice(-2)
    return countryCode.toUpperCase()
  }
  return 'INT'
}

const getLanguageLocaleCode = (locale: string): string => {
  if (locale && locale.length > 2) return locale.substring(0, 2)
  else return locale
}

const getCurrentLocale = (locale: string): string => {
  if (locale && locale.length > 2) {
    const language = locale.slice(-2)
    return `${locale.substring(0, 2)}-${language.toUpperCase()}`
  }
  return locale
}

const getContentfulId = (pathname: string): string => {
  if (pathname.includes('confirmation')) return 'Confirmation'
  else if (pathname.includes('yourbooking')) return 'BuildYourBooking'
  else if (pathname.includes('extras')) return 'Extras'
  else if (pathname.includes('pets')) return 'Pets'
  else if (pathname.includes('pending')) return 'Pending'
  else if (pathname.includes('passengers')) return 'Passengers'
  else if (pathname.includes('checkout')) return 'Checkout'
  else if (pathname.includes('review')) return 'Review'
  return 'Invalid contentful id'
}
